import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../../../contexts/Theme/ThemeContext.tsx";
import { VariacaoV2, NivelVariacao } from "../../../../types/Produto.ts";
import ImgWithLoading from "../../../../components/ImgWithLoading/ImgWithLoading.tsx";
import { ReactComponent as PlusIcon } from "../../../../assets/newImages/icons/plus.svg";
import { MockupProdutoContainer } from "./styles.ts";

interface MockupProdutoProps {
  produto: {
    nome: string;
    referencia: string;
    descricao: string;
    fotos: any[];
  };
  variacoes: VariacaoV2[];
  niveisVariacao: NivelVariacao[];
  listasDePreco: any[];
  temEstoque: boolean;
  temDestaque: boolean;
  tagDestaque: string;
}

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function formatPreco(value: string | number): string {
  const raw = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(raw)) return "R$ 0,00";
  const reais = raw / 100;
  return reais.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export const MockupProduto: React.FC<MockupProdutoProps> = ({
  produto,
  variacoes,
  niveisVariacao,
  temEstoque,
  temDestaque,
  tagDestaque,
}) => {
  const { theme } = useTheme();

  const variacoesAtivas = useMemo(
    () => variacoes.filter((v) => Number(v.ativo) === 1),
    [variacoes]
  );

  const valoresPrincipais = useMemo(() => {
    if (!niveisVariacao.length) return [];
    return niveisVariacao[0]?.valores || [];
  }, [niveisVariacao]);

  const [selectedPill, setSelectedPill] = useState(0);
  const [currentFotoIndex, setCurrentFotoIndex] = useState(0);

  // Drag-to-scroll for pills on desktop
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (trackRef.current?.offsetLeft || 0);
    scrollLeft.current = trackRef.current?.scrollLeft || 0;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !trackRef.current) return;
    e.preventDefault();
    const x = e.pageX - (trackRef.current.offsetLeft || 0);
    trackRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const variacoesFiltradas = useMemo(() => {
    if (!valoresPrincipais.length) return variacoesAtivas;
    const valor = valoresPrincipais[selectedPill];
    if (!valor) return variacoesAtivas;
    return variacoesAtivas.filter((v) => v.valor_principal === valor);
  }, [variacoesAtivas, valoresPrincipais, selectedPill]);

  const variacaoSelecionada = variacoesFiltradas[0] || null;

  // eslint-disable-next-line eqeqeq
  const fotos = produto.fotos?.filter((f: any) => f.tipo == 1 || (!f.tipo && f.tipo !== 0)) || [];
  const totalFotos = fotos.length;

  // Sincronizar pill com a foto atual
  const syncPillWithFoto = useCallback((fotoIndex: number) => {
    const foto = fotos[fotoIndex];
    if (foto?.valor_variacao && valoresPrincipais.length > 0) {
      const pillIndex = valoresPrincipais.indexOf(foto.valor_variacao);
      if (pillIndex !== -1) setSelectedPill(pillIndex);
    }
  }, [fotos, valoresPrincipais]);

  // Auto-scroll das pills para centralizar a selecionada
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const btn = track.children[selectedPill] as HTMLElement;
    if (!btn) return;
    btn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedPill]);

  const handlePrev = () => {
    const next = currentFotoIndex > 0 ? currentFotoIndex - 1 : totalFotos - 1;
    setCurrentFotoIndex(next);
    syncPillWithFoto(next);
  };

  const handleNext = () => {
    const next = currentFotoIndex < totalFotos - 1 ? currentFotoIndex + 1 : 0;
    setCurrentFotoIndex(next);
    syncPillWithFoto(next);
  };

  const fotoAtual = fotos[currentFotoIndex];
  const fotoSrc = fotoAtual
    ? fotoAtual.imagem_externa_2
      ? fotoAtual.imagem_externa_2
      : fotoAtual.imagem_externa
        ? fotoAtual.imagem_externa
        : fotoAtual.base64 || ""
    : "";

  const preco = variacaoSelecionada?.precos?.[0]?.preco;
  const descricaoTexto = stripHtml(produto.descricao || "");

  return (
    <MockupProdutoContainer theme={theme}>
      <div className="mockup-card">
        {/* === Photo area === */}
        <div className="mockup-foto-area">
          <div className="mockup-foto-wrapper">
            {totalFotos > 0 && fotoSrc ? (
              <ImgWithLoading
                src={fotoSrc}
                alt={produto.nome || ""}
              />
            ) : (
              <div className="no-image">Sem imagem</div>
            )}
          </div>

          {/* Carousel arrows */}
          {totalFotos > 1 && (
            <>
              <button className="carousel-btn prev" onClick={handlePrev}>
                &#8249;
              </button>
              <button className="carousel-btn next" onClick={handleNext}>
                &#8250;
              </button>
            </>
          )}

          {/* Badges */}
          {temDestaque && tagDestaque && (
            <div className="badges">
              <div className="badge fonteRotulos">{tagDestaque}</div>
            </div>
          )}
        </div>

        {/* === Below photo: counter + dots | share === */}
        <div className="mockup-below-foto">
          <div className="foto-counter">
            <span className="counter-text fonteRotulos">
              {totalFotos > 0 ? `${currentFotoIndex + 1}/${totalFotos}` : "0/0"}
            </span>
            {totalFotos > 1 && (
              <div className="dots">
                {fotos.map((_: any, i: number) => (
                  <div
                    key={i}
                    className={`dot ${i === currentFotoIndex ? "active" : ""}`}
                    onClick={() => {
                      setCurrentFotoIndex(i);
                      syncPillWithFoto(i);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="mockup-share">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 11" fill="none">
              <path d="M8.08052 6.57232L7.13964 6.12955C7.08671 6.09792 7.02426 6.08612 6.96344 6.09626C6.90262 6.10639 6.84737 6.13781 6.80756 6.1849C6.69687 6.29559 6.47549 6.62766 6.36479 6.73836C6.2541 6.84905 6.19876 6.84905 6.08806 6.7937C5.68923 6.62639 5.31645 6.40273 4.98114 6.12955C4.69338 5.84141 4.43391 5.52634 4.2063 5.18867C4.18565 5.1425 4.17988 5.09106 4.1898 5.04147C4.19972 4.99188 4.22483 4.94661 4.26164 4.91194C4.31699 4.85659 4.37234 4.7459 4.48303 4.69055C4.54129 4.62953 4.57986 4.55239 4.59372 4.46917C4.62095 4.43873 4.636 4.39932 4.636 4.35848C4.636 4.31763 4.62095 4.27822 4.59372 4.24778C4.42784 3.92414 4.28002 3.59156 4.15095 3.25155C4.09561 2.86413 3.98491 2.86413 3.87422 2.86413H3.59749C3.47265 2.88492 3.35695 2.94277 3.26542 3.03017C3.1075 3.17968 2.98184 3.35992 2.89617 3.5598C2.81051 3.75968 2.76665 3.97497 2.7673 4.19244C2.81857 4.71325 3.00982 5.21049 3.32076 5.63143C3.89937 6.50891 4.70188 7.21588 5.6453 7.67924C5.89423 7.79582 6.15368 7.88848 6.42014 7.95597C6.70401 8.05846 7.01126 8.07766 7.30568 8.01131C7.4999 7.97168 7.6838 7.8922 7.84575 7.77788C8.0077 7.66357 8.14417 7.5169 8.24656 7.34716C8.34696 7.14036 8.36669 6.90357 8.30191 6.68301L8.08052 6.57232ZM9.46417 1.53583C8.51049 0.623248 7.26205 0.0815581 5.94411 0.00849103C4.62616 -0.064576 3.3255 0.335792 2.27678 1.13736C1.22807 1.93892 0.500357 3.08891 0.224948 4.37982C-0.0504611 5.67074 0.144567 7.01758 0.774845 8.17735L0 11L2.93334 10.2252C3.76703 10.6785 4.70437 10.9071 5.65318 10.8885C6.60199 10.8698 7.52961 10.6045 8.34484 10.1188C9.16007 9.63297 9.83484 8.94338 10.3028 8.11779C10.7708 7.29221 11.0158 6.35904 11.0139 5.41005C11.026 4.6937 10.8952 3.98213 10.6291 3.31692C10.363 2.65171 9.96697 2.04622 9.46417 1.53583ZM7.96983 9.28427C7.2407 9.74571 6.39743 9.99486 5.5346 10.0038C4.72098 9.99887 3.92164 9.78952 3.21007 9.39497L3.04403 9.28427L1.3283 9.72704L1.77107 8.06666L1.66038 7.90062C1.02146 6.85662 0.82002 5.60278 1.09978 4.41118C1.37955 3.21959 2.11794 2.1864 3.15472 1.53583C3.65836 1.21459 4.22092 0.996848 4.80959 0.895306C5.39826 0.793764 6.00125 0.810453 6.5834 0.944399C7.16556 1.07835 7.71521 1.32687 8.20031 1.67547C8.6854 2.02408 9.09623 2.46578 9.40882 2.97482C9.72993 3.48321 9.94746 4.05001 10.0489 4.64269C10.1504 5.23537 10.1338 5.84225 10.0001 6.4285C9.86639 7.01474 9.61821 7.56881 9.2698 8.05888C8.92139 8.54896 8.47961 8.96539 7.96983 9.28427Z" fill="#4A4A4A"/>
            </svg>
            <span className="fonteRotulos">Compartilhar</span>
          </div>
        </div>

        {/* === Description section === */}
        <div className="mockup-descricao-section">
          <div className="mockup-nome-descricao">
            <div className="mockup-titulo-sku">
              <div className="mockup-nome fonteTexto">
                {produto.nome || "Nome do produto"}
              </div>
              {produto.referencia && (
                <div className="mockup-sku fonteRotulos">SKU {produto.referencia}</div>
              )}
            </div>
            {descricaoTexto && (
              <div className="mockup-descricao fonteRotulos">{descricaoTexto}</div>
            )}
          </div>

          {/* Separator */}
          <hr className="mockup-separator" />

          {/* Variation pills + detail — layout depends on whether there's a secondary level */}
          {valoresPrincipais.length > 0 && niveisVariacao.length > 1 && (
            <>
              <div className="mockup-pills-slider">
                <div
                  className="mockup-pills-track"
                  ref={trackRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  {valoresPrincipais.map((valor, i) => (
                    <button
                      key={i}
                      className={`pill fonteTexto ${i === selectedPill ? "active" : ""}`}
                      onClick={() => {
                        setSelectedPill(i);
                        const valor = valoresPrincipais[i];
                        if (valor) {
                          const fotoIdx = fotos.findIndex((f: any) => f.valor_variacao === valor);
                          if (fotoIdx !== -1) setCurrentFotoIndex(fotoIdx);
                        }
                      }}
                    >
                      {valor}
                    </button>
                  ))}
                </div>
              </div>

              {variacaoSelecionada && (
                <div className="mockup-variacao-bloco">
                  <div className="variacao-left">
                    <span className="variacao-nome fonteTexto">
                      {variacaoSelecionada.valor_secundario || variacaoSelecionada.variacao}
                    </span>
                    <div className="variacao-detalhes fonteRotulos">
                      {temEstoque && variacaoSelecionada.estoque != null && (
                        <span>Estoque: {variacaoSelecionada.estoque}</span>
                      )}
                      {temEstoque &&
                        variacaoSelecionada.estoque != null &&
                        variacaoSelecionada.peso != null &&
                        variacaoSelecionada.peso !== "" && <span>|</span>}
                      {variacaoSelecionada.peso != null &&
                        variacaoSelecionada.peso !== "" && (
                          <span>Peso {variacaoSelecionada.peso}g</span>
                        )}
                    </div>
                    <div className="mockup-precos">
                      <span className="preco-por fonteTexto">{formatPreco(preco || 0)}</span>
                    </div>
                  </div>
                  <div className="btn-add"><PlusIcon /></div>
                </div>
              )}

              {!variacaoSelecionada && (
                <div className="mockup-variacao-bloco">
                  <div className="variacao-left">
                    <div className="mockup-precos">
                      <span className="preco-por fonteTexto">R$ 0,00</span>
                    </div>
                  </div>
                  <div className="btn-add"><PlusIcon /></div>
                </div>
              )}
            </>
          )}

          {/* Single level only: show variations stacked, max 2 */}
          {valoresPrincipais.length > 0 && niveisVariacao.length === 1 && (
            <div className="mockup-variacoes-lista">
              {variacoesAtivas.slice(0, 2).map((v, i) => {
                const vPreco = v.precos?.[0]?.preco;
                return (
                  <div className="mockup-variacao-bloco" key={i}>
                    <div className="variacao-left">
                      <span className="variacao-nome fonteTexto">{v.variacao}</span>
                      <div className="variacao-detalhes fonteRotulos">
                        {temEstoque && v.estoque != null && (
                          <span>Estoque: {v.estoque}</span>
                        )}
                        {temEstoque && v.estoque != null && v.peso != null && v.peso !== "" && (
                          <span>|</span>
                        )}
                        {v.peso != null && v.peso !== "" && (
                          <span>Peso {v.peso}g</span>
                        )}
                      </div>
                      <div className="mockup-precos">
                        <span className="preco-por fonteTexto">{formatPreco(vPreco || 0)}</span>
                      </div>
                    </div>
                    <div className="btn-add"><PlusIcon /></div>
                  </div>
                );
              })}
              {variacoesAtivas.length > 2 && (
                <span className="mockup-mais-variacoes fonteRotulos">
                  +{variacoesAtivas.length - 2} variações
                </span>
              )}
            </div>
          )}

          {/* No variations at all */}
          {valoresPrincipais.length === 0 && (
            <div className="mockup-variacao-bloco">
              <div className="variacao-left">
                <div className="mockup-precos">
                  <span className="preco-por fonteTexto">R$ 0,00</span>
                </div>
              </div>
              <div className="btn-add"><PlusIcon /></div>
            </div>
          )}
        </div>
      </div>
    </MockupProdutoContainer>
  );
};
